import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Edit, Trash2, Plus, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import PhotoPositioner from '../../components/PhotoPositioner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMembers = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [members, setMembers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    instrument: '',
    bio: '',
    favorite_artist: '',
    image: '',
    order: 0,
    image_position_x: 50,
    image_position_y: 50
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchMembers();
    }
  }, [user, loading, navigate]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/band-members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load band members');
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const openDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        instrument: member.instrument,
        bio: member.bio,
        favorite_artist: member.favorite_artist || '',
        image: member.image || '',
        order: member.order,
        image_position_x: member.image_position_x || 50,
        image_position_y: member.image_position_y || 50
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        instrument: '',
        bio: '',
        favorite_artist: '',
        image: '',
        order: members.length,
        image_position_x: 50,
        image_position_y: 50
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingMember(null);
    setFormData({
      name: '',
      instrument: '',
      bio: '',
      favorite_artist: '',
      image: '',
      order: 0,
      image_position_x: 50,
      image_position_y: 50
    });
  };

  const handlePositionChange = (position) => {
    setFormData({ 
      ...formData, 
      image_position_x: position.x, 
      image_position_y: position.y 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        image: formData.image
      };
      
      if (editingMember) {
        await axios.put(
          `${API}/band-members/${editingMember.id}`,
          dataToSend,
          { withCredentials: true }
        );
        toast.success('Band member updated successfully');
      } else {
        await axios.post(
          `${API}/band-members`,
          dataToSend,
          { withCredentials: true }
        );
        toast.success('Band member created successfully');
      }
      closeDialog();
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Failed to save band member');
    }
  };

  const handleDelete = async (member) => {
    // Enhanced confirmation dialog
    const confirmMessage = `Are you sure you want to delete "${member.name}"?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      console.log('Attempting to delete member:', member.id);
      
      const response = await axios.delete(`${API}/band-members/${member.id}`, { 
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response:', response.data);
      toast.success(`${member.name} deleted successfully`);
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
        navigate('/admin/login');
      } else if (error.response?.status === 404) {
        toast.error('Band member not found');
        fetchMembers(); // Refresh the list
      } else {
        toast.error(`Failed to delete ${member.name}: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <div className="admin-page-header">
            <Button className="admin-add-button" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="button-icon" />
              Back to Dashboard
            </Button>
          </div>

          <div className="admin-title-row">
            <h1 className="admin-page-title">Manage Band Members</h1>
            <Button className="admin-add-button" onClick={() => openDialog()}>
              <Plus className="button-icon" />
              Add Member
            </Button>
          </div>

          <div className="admin-members-grid">
            {members.length === 0 ? (
              <p className="admin-empty-message">No band members yet. Add your first member!</p>
            ) : (
              members.map((member) => (
                <Card key={member.id} className="admin-member-card">
                  <CardContent className="admin-member-card-content">
                    <h3 className="admin-member-name">{member.name}</h3>
                    <p className="admin-member-instrument">{member.instrument}</p>
                    <p className="admin-member-bio">{member.bio}</p>
                    {member.favorite_artist && (
                      <p className="admin-member-favorite"><strong>Favorite:</strong> {member.favorite_artist}</p>
                    )}
                    <div className="admin-member-actions">
                      <Button variant="outline" size="sm" onClick={() => openDialog(member)}>
                        <Edit className="button-icon-sm" />
                        Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(member)}>
                        <Trash2 className="button-icon-sm" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Edit Band Member' : 'Add New Band Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Name *</label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Johnny Thunder"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Instrument *</label>
              <Input
                required
                value={formData.instrument}
                onChange={(e) => setFormData({ ...formData, instrument: e.target.value })}
                placeholder="Lead Vocals & Rhythm Guitar"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Bio *</label>
              <Textarea
                required
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about this band member..."
                rows={4}
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Favorite 50s Artist</label>
              <Input
                value={formData.favorite_artist}
                onChange={(e) => setFormData({ ...formData, favorite_artist: e.target.value })}
                placeholder="Elvis Presley"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Image URL</label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="/api/uploads/filename.jpg"
              />
              <p style={{ fontSize: '0.85rem', color: 'var(--retro-cream)', opacity: 0.8, marginTop: '0.5rem' }}>
                💡 For new photos, send images to your developer to add to the system. Use relative paths like "/api/uploads/filename.jpg"
              </p>
            </div>
            {formData.image && (
              <div className="admin-form-group">
                <PhotoPositioner
                  imageUrl={formData.image}
                  onPositionChange={handlePositionChange}
                  initialX={formData.image_position_x}
                  initialY={formData.image_position_y}
                />
              </div>
            )}
            <div className="admin-form-group">
              <label className="admin-form-label">Display Order</label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                placeholder="0"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingMember ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMembers;
